import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { type StatusBarItem, useWorkspacePage } from "@/components/workspace";
import { diagnosticsClient, formatBytes, shortId } from "@/lib/diagnostics";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Radio } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/diagnostics/live")({
	component: DiagnosticsLivePage,
});

function DiagnosticsLivePage() {
	const { t } = useTranslation("diagnostics");
	const navigate = useNavigate();
	useBreadcrumbs([
		{ label: t("title"), href: "/diagnostics" },
		{ label: t("sidebar_live") },
	]);

	const healthQuery = useQuery({
		queryKey: ["diagnostics", "health"],
		queryFn: () => diagnosticsClient.getDiagnosticsHealth({}),
		refetchInterval: 5000,
	});

	const employeesQuery = useQuery({
		queryKey: ["diagnostics", "live-employees"],
		queryFn: () => diagnosticsClient.getLiveEmployees({}),
		refetchInterval: 5000,
	});

	const health = healthQuery.data?.stats;
	const employees = employeesQuery.data?.employees ?? [];

	// ── Auto-refresh elapsed counter ───────────────────────────────
	const [secondsAgo, setSecondsAgo] = useState(0);
	const latestUpdate = Math.max(
		healthQuery.dataUpdatedAt ?? 0,
		employeesQuery.dataUpdatedAt ?? 0,
	);

	useEffect(() => {
		if (!latestUpdate) return;
		setSecondsAgo(0);
		const interval = setInterval(() => {
			setSecondsAgo(Math.floor((Date.now() - latestUpdate) / 1000));
		}, 1000);
		return () => clearInterval(interval);
	}, [latestUpdate]);

	// ── Helpers ────────────────────────────────────────────────────
	const statusBadge = (activity: string) => {
		switch (activity) {
			case "recording":
				return (
					<Badge
						variant="default"
						className="text-[9px] h-4 px-1.5 bg-green-600 hover:bg-green-600"
					>
						{t("live_activity_recording", { defaultValue: "Запись" })}
					</Badge>
				);
			case "active":
				return (
					<Badge variant="secondary" className="text-[9px] h-4 px-1.5">
						{t("live_activity_active", { defaultValue: "Активен" })}
					</Badge>
				);
			default:
				return (
					<Badge variant="outline" className="text-[9px] h-4 px-1.5">
						{t("live_activity_offline", { defaultValue: "Офлайн" })}
					</Badge>
				);
		}
	};

	const borderColor = (activity: string) => {
		switch (activity) {
			case "recording":
				return "border-l-green-500";
			case "active":
				return "border-l-blue-500";
			default:
				return "border-l-muted-foreground/30";
		}
	};

	const formatEventTime = (emp: (typeof employees)[0]) => {
		if (emp.updatedAt) {
			return new Date(Number(emp.updatedAt.seconds) * 1000).toLocaleTimeString(
				"ru-RU",
			);
		}
		return "\u2014";
	};

	// ── Status bar ─────────────────────────────────────────────────
	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "updated",
				content: t("live_status_updated", {
					defaultValue: "Обновлено {{time}}",
					time: new Date().toLocaleTimeString("ru-RU"),
				}),
				align: "left" as const,
			},
			{
				id: "active",
				content: t("live_status_active", {
					num: Number(health?.activeSessions ?? 0),
				}),
				align: "left" as const,
			},
		],
		[t, health?.activeSessions, latestUpdate],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<div className="flex flex-col h-full overflow-hidden">
			{/* ── Health KPI strip ─────────────────────────────────── */}
			<div className="grid grid-cols-6 gap-px bg-border border-b shrink-0">
				{healthQuery.isLoading ? (
					Array.from({ length: 6 }).map((_, i) => (
						<div key={`h-${i}`} className="bg-background px-3 py-1.5">
							<Skeleton className="h-3 w-16 mb-1" />
							<Skeleton className="h-5 w-12" />
						</div>
					))
				) : !health ? (
					<div className="col-span-6 bg-background px-3 py-2 text-xs text-muted-foreground text-center">
						{t("live_no_health", {
							defaultValue: "Нет данных о здоровье системы",
						})}
					</div>
				) : (
					<>
						<div className="bg-background px-3 py-1.5">
							<p className="text-[10px] text-muted-foreground">
								{t("live_kpi_active", { defaultValue: "Активные" })}
							</p>
							<p className="text-sm font-semibold tabular-nums">
								{Number(health.activeSessions)}
							</p>
						</div>
						<div className="bg-background px-3 py-1.5">
							<p className="text-[10px] text-muted-foreground">
								{t("live_kpi_started", { defaultValue: "Начато" })}
							</p>
							<p className="text-sm font-semibold tabular-nums">
								{Number(health.sessionsStarted)}
							</p>
						</div>
						<div className="bg-background px-3 py-1.5">
							<p className="text-[10px] text-muted-foreground">
								{t("live_kpi_completed", { defaultValue: "Завершено" })}
							</p>
							<p className="text-sm font-semibold tabular-nums">
								{Number(health.sessionsCompleted)}
							</p>
						</div>
						<div className="bg-background px-3 py-1.5">
							<p className="text-[10px] text-muted-foreground">
								{t("live_kpi_bytes", { defaultValue: "Загружено" })}
							</p>
							<p className="text-sm font-semibold tabular-nums">
								{formatBytes(Number(health.totalBytesUploaded))}
							</p>
						</div>
						<div className="bg-background px-3 py-1.5">
							<p className="text-[10px] text-muted-foreground">
								{t("live_kpi_segments", { defaultValue: "Сегменты" })}
							</p>
							<p className="text-sm font-semibold tabular-nums">
								{Number(health.totalSegments).toLocaleString()}
							</p>
						</div>
						<div className="bg-background px-3 py-1.5">
							<p className="text-[10px] text-muted-foreground">
								{t("live_kpi_heartbeat", { defaultValue: "Пульс" })}
							</p>
							<p className="text-xs font-medium tabular-nums">
								{health.lastHeartbeatAt
									? new Date(
											Number(health.lastHeartbeatAt.seconds) * 1000,
										).toLocaleTimeString("ru-RU")
									: "\u2014"}
							</p>
						</div>
					</>
				)}
			</div>

			{/* ── Auto-refresh indicator ──────────────────────────── */}
			{latestUpdate > 0 && (
				<div className="flex items-center justify-end px-3 py-1 border-b bg-muted/30 shrink-0">
					<Badge
						variant="outline"
						className="text-[10px] h-4 px-1.5 font-normal text-muted-foreground"
					>
						{t("live_refresh_ago", { seconds: secondsAgo })}
					</Badge>
				</div>
			)}

			{/* ── Employee monitoring grid ────────────────────────── */}
			<div className="flex-1 overflow-y-auto p-3">
				{employeesQuery.isLoading ? (
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
						{Array.from({ length: 6 }).map((_, i) => (
							<Card key={`e-${i}`} className="p-3 border-l-4 border-l-muted">
								<div className="flex items-center gap-2 mb-2">
									<Skeleton className="h-4 w-16" />
									<Skeleton className="h-4 w-14" />
								</div>
								<Skeleton className="h-3 w-full mb-1.5" />
								<Skeleton className="h-3 w-3/4 mb-1.5" />
								<div className="flex justify-between mt-2">
									<Skeleton className="h-3 w-16" />
									<Skeleton className="h-3 w-12" />
								</div>
							</Card>
						))}
					</div>
				) : employees.length === 0 ? (
					<EmptyState
						icon={Radio}
						title={t("live_empty_title", {
							defaultValue: "Нет активных сотрудников",
						})}
						description={t("live_empty_auto", {
							defaultValue:
								"Устройства подключаются автоматически. Активные сотрудники появятся здесь.",
						})}
					/>
				) : (
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
						{employees.map((emp) => (
							<Card
								key={`${emp.employeeId}-${emp.deviceId}`}
								className={`p-3 border-l-4 ${borderColor(emp.activity)} cursor-pointer hover:shadow-md transition-shadow`}
								onClick={() =>
									navigate({
										to: "/diagnostics",
										search: { q: undefined, employee: emp.employeeId },
									})
								}
							>
								{/* Row 1: ID + status badge */}
								<div className="flex items-center justify-between mb-1.5">
									<span className="text-xs font-semibold font-mono">
										{shortId(emp.employeeId)}
									</span>
									{statusBadge(emp.activity)}
								</div>

								{/* Row 2: App name / window title */}
								{emp.lastEvent && (
									<p className="text-[11px] text-foreground truncate mb-0.5">
										{emp.lastEvent}
									</p>
								)}
								{emp.captureProfile && (
									<p className="text-[10px] text-muted-foreground truncate mb-1.5">
										{emp.captureProfile}
									</p>
								)}

								{/* Row 3: Stats */}
								<div className="flex items-center justify-between text-[10px] text-muted-foreground pt-1.5 border-t border-border/50">
									<div className="flex items-center gap-2">
										<span>{formatBytes(Number(emp.bytesUploaded))}</span>
										<span className="text-border">|</span>
										<span>
											{Number(emp.segmentCount)}{" "}
											{t("live_card_segments", { defaultValue: "сегм." })}
										</span>
									</div>
									<span className="tabular-nums">{formatEventTime(emp)}</span>
								</div>
							</Card>
						))}
					</div>
				)}
			</div>
		</div>
	);
}
